import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, TextField, Alert, Box
} from '@mui/material';
import { Numbers } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Enrollment, SessionClass, SectionInstance } from '../../types/sessions';
import { enrollmentsApi } from '../../services/sessions/enrollmentsApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';

interface ChangeRollNumberDialogProps {
  open: boolean;
  enrollment: Enrollment | null;
  classes: SessionClass[];
  onClose: () => void;
  onChanged: () => void;
}

/**
 * Focused modal for editing the roll number of the current enrollment,
 * without moving the student to another class or section. Same override
 * pattern as TransferClassDialog — prefilled with the current roll,
 * autofocus + select-all so admins can just re-type over it.
 *
 * Collision on the target slot's partial unique index surfaces as a 409;
 * we render the error inline instead of a snackbar so the admin can
 * correct the number in place.
 */
const ChangeRollNumberDialog: React.FC<ChangeRollNumberDialogProps> = ({
  open, enrollment, classes, onClose, onChanged
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rollInput, setRollInput] = useState<string>('');
  const [rollError, setRollError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [section, setSection] = useState<SectionInstance | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open || !enrollment) {
      setRollInput('');
      setRollError(null);
      setSection(null);
      return;
    }
    setRollInput(String(enrollment.rollNumber));
    setRollError(null);
    // Autofocus + select-all so admin can type over the prefilled value
    // instantly. Delayed one frame for the dialog to mount its input.
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }, [open, enrollment]);

  // Resolve the enrollment's section name so the summary reads well
  // ("Roll 5 in Class 12 – A") without asking the caller to plumb it.
  useEffect(() => {
    if (!open || !enrollment?.sectionInstanceId) {
      setSection(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await sectionInstancesApi.list({
          sessionId: enrollment.sessionId,
          classId: enrollment.classId
        });
        if (cancelled) return;
        setSection(list.find(s => String(s._id) === String(enrollment.sectionInstanceId)) ?? null);
      } catch { /* silent — summary just omits section */ }
    })();
    return () => { cancelled = true; };
  }, [open, enrollment]);

  if (!enrollment) return null;

  const currentClass = classes.find(c => c._id === String(enrollment.classId));
  const slotLabel = `${currentClass?.name ?? 'Class'}${section ? ` – ${section.name}` : ''}`;

  const parsed = rollInput.trim() === '' ? null : Number(rollInput);
  const isValid = parsed !== null && Number.isInteger(parsed) && parsed >= 1;
  const isNoChange = parsed === enrollment.rollNumber;

  const submit = async () => {
    if (!isValid) {
      setRollError('Enter a positive whole number');
      inputRef.current?.focus();
      return;
    }
    if (isNoChange) {
      // No mutation — just close silently.
      onClose();
      return;
    }
    setSubmitting(true);
    setRollError(null);
    try {
      const result = await enrollmentsApi.update(enrollment._id, { rollNumber: parsed! });
      enqueueSnackbar(
        `Roll updated to ${result.rollNumber} in ${slotLabel}`,
        { variant: 'success' }
      );
      onChanged();
      onClose();
    } catch (err: any) {
      const msg: string = err?.message || 'Could not change roll number';
      if (/already taken/i.test(msg)) {
        setRollError(`Roll ${parsed} is already taken in ${slotLabel}. Pick another number.`);
        inputRef.current?.focus();
        inputRef.current?.select();
      } else {
        enqueueSnackbar(msg, { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Numbers color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={600}>Change roll number</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Reassigns the roll in the current slot. Class and section are unchanged.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined">
            Currently <strong>Roll {enrollment.rollNumber}</strong> in <strong>{slotLabel}</strong>.
          </Alert>

          <TextField
            inputRef={inputRef}
            label="New roll number"
            value={rollInput}
            onChange={(e) => {
              const v = e.target.value.replace(/[^\d]/g, '');
              setRollInput(v);
              if (rollError) setRollError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submit(); }
            }}
            error={Boolean(rollError)}
            helperText={
              rollError
                ? rollError
                : 'Must be a positive integer, unique within the current class and section.'
            }
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            fullWidth
            autoFocus
          />

          {isValid && !isNoChange && !rollError && (
            <Box sx={{ pl: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Will change from <strong>Roll {enrollment.rollNumber}</strong> to <strong>Roll {parsed}</strong>. The change is recorded on this enrollment's history.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          disableElevation
          disabled={submitting || !isValid || isNoChange}
          onClick={submit}
        >
          {submitting ? 'Saving…' : 'Save roll number'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeRollNumberDialog;
